import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { FormParagraphFormGroupField } from '@frontend/components/fields/FormParagraphField';
import type { ValidationErrors } from '../../../../utils/validation';
import { serviceConsumerContactDetailsSchema } from '../../../../../common/validation/serviceConsumer';
import { fetchJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { handleStateObjectChange } from '../../../../utils/handleStateObjectChange';
import { emptyValidationErrors, validate, validateOne } from '../../../../utils/validation';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { InputTextField } from '../../../userDataManagement/InputText';
import { SaveButton } from '../../../userDataManagement/SaveButton';

interface IGgzConsultContactDetailsProps {
    isReadOnly: boolean;
    serviceConsumerId: string;
}

interface IServiceConsumerContactDetails {
    phoneNumberConsult: string | null;
}

export function GgzConsultContactDetails(props: IGgzConsultContactDetailsProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [serviceConsumerContactDetails, setServiceConsumerContactDetails] =
        useState<IServiceConsumerContactDetails>();

    useEffect(() => {
        load();
    }, [props.serviceConsumerId]);

    if (!serviceConsumerContactDetails) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'personalSettings',
                item: serviceConsumerContactDetails,
                errors: errors,
                onChange: async (key, value) =>
                    handleStateObjectChange(
                        serviceConsumerContactDetails,
                        setServiceConsumerContactDetails,
                        key,
                        value
                    ),
                onDoValidate: async (key) =>
                    validateOne(
                        serviceConsumerContactDetailsSchema,
                        serviceConsumerContactDetails,
                        key,
                        errors,
                        setErrors
                    ),
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !props.isReadOnly && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('personalSettings.contactDetails')}
                />
                <Card.Body>
                    <Form
                        onSubmit={async (e) => {
                            e.preventDefault();
                            await handleSubmit();
                        }}
                        submitOnEnter={false}
                    >
                        {isEditing ? (
                            <InputTextField
                                error={errors.get('phoneNumberConsult')}
                                fieldKey="phoneNumberConsult"
                                hasErrors={errors.has('phoneNumberConsult')}
                                label="common.phoneNumber"
                                nullable={true}
                            />
                        ) : (
                            <FormParagraphFormGroupField
                                fieldKey="phoneNumberConsult"
                                label="common.phoneNumber"
                            />
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={handleCancel}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    async function load() {
        setErrors(emptyValidationErrors);

        const serviceConsumer = (await fetchJson(`/api/serviceConsumers/${props.serviceConsumerId}`, 'GET')).json;

        setServiceConsumerContactDetails(serviceConsumer);
    }

    async function handleCancel() {
        await load();
        setIsEditing(false);
    }

    async function handleSubmit() {
        setIsSaving(true);

        if (serviceConsumerContactDetails) {
            const { errors: validationErrors, item } = validate(
                serviceConsumerContactDetailsSchema,
                serviceConsumerContactDetails
            );
            setErrors(validationErrors.size > 0 ? validationErrors : emptyValidationErrors);

            if (validationErrors.size > 0) {
                setIsSaving(false);

                return;
            }

            await fetchJson(`/api/serviceConsumers/${props.serviceConsumerId}/contactDetails`, 'POST', item);
        }

        setIsEditing(false);
        setIsSaving(false);
    }
}
